import { i18n } from '../i18n.js';
import { showAchievementBanner } from './banner.js';
import { MoonfallCloud } from '../convex-client.js';
/**
 * Crash-safe First Stellar banner coordinator.
 *
 * Collection and Foundry share one authenticated claim. Convex keeps the
 * achievement pending until the shared presenter returns a connected DOM node,
 * then this module acknowledges the exact delivery receipt. An unmounted
 * receipt is released; a crashed page recovers after the server lease expires.
 */

import { Firebase } from '../firebase-loader.js';
let StellarFirstBannerDelivery = {};

StellarFirstBannerDelivery = (function () {
    const PAGE_SIZE = 32;
    const PAGE_YIELD_INTERVAL = 32;
    const ACK_RETRY_MS = 500;
    const CLAIM_RETRY_MS = 500;
    const BANNER_SPACING_MS = 4500;
    const LEASE_RETRY_MS = 60_100;
    let activeRun = null;
    let delayState = null;
    const followups = new Map();

    function uiText(key, fallback, replacements = {}) {
        const translated = i18n?.t?.(key, replacements);
        const template = translated && translated !== key ? translated : fallback;
        return Object.entries(replacements).reduce((text, [name, value]) => (
            String(text)
                .replace(new RegExp(`\\{\\{${name}\\}\\}`, 'g'), String(value))
                .replace(new RegExp(`\\{${name}\\}`, 'g'), String(value))
        ), String(template));
    }

    function firebaseUid() {
        const uid = Firebase?.getUser?.()?.uid;
        return typeof uid === 'string' && uid ? uid : null;
    }

    function identityFor(requester) {
        const uid = firebaseUid();
        if (uid) return `firebase:${uid}`;
        return requester?.profileId ? `profile:${requester.profileId}` : null;
    }

    function isRequesterIdentityCurrent(requester, identity) {
        if (!requester || identityFor(requester) !== identity) return false;
        try {
            return requester.isIdentityCurrent?.() !== false;
        } catch (_) {
            return false;
        }
    }

    function isRequesterActive(requester, identity) {
        if (!isRequesterIdentityCurrent(requester, identity)) return false;
        try {
            return requester.isActive?.() !== false;
        } catch (_) {
            return false;
        }
    }

    function createDeliveryId() {
        try {
            if (typeof window.crypto?.randomUUID === 'function') {
                return window.crypto.randomUUID();
            }
            if (typeof window.crypto?.getRandomValues === 'function') {
                const bytes = new Uint8Array(16);
                window.crypto.getRandomValues(bytes);
                return `stellar-${Array.from(bytes, (byte) => (
                    byte.toString(16).padStart(2, '0')
                )).join('')}`;
            }
        } catch (_) {
            // A delivery id is an idempotency key, not a credential.
        }
        return `stellar-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 18)}`;
    }

    function wait(delayMs) {
        return new Promise((resolve) => {
            if (delayState) {
                clearTimeout(delayState.timer);
                delayState.resolve();
            }
            const state = {
                resolve: () => {
                    if (delayState === state) delayState = null;
                    resolve();
                },
                timer: null,
            };
            state.timer = setTimeout(state.resolve, delayMs);
            delayState = state;
        });
    }

    function activeRequesters(run) {
        return [...run.requesters.values()].filter((requester) => (
            isRequesterActive(requester, run.identity)
        ));
    }

    function mutateReceipt(name, args) {
        return MoonfallCloud.convex.receiptMutation(name, args);
    }

    async function release(run, banner) {
        if (!isRequesterIdentityCurrent(run.primaryRequester, run.identity)) return false;
        try {
            const result = await mutateReceipt(
                'stellar:releaseStellarFirstBanner',
                {
                    cardId: banner.cardId,
                    deliveryId: run.deliveryId,
                },
            );
            return result?.released === true;
        } catch (error) {
            console.warn('[First Stellar] Failed to release unmounted delivery:', error);
            return false;
        }
    }

    async function acknowledge(run, banner) {
        for (let attempt = 0; attempt < 2; attempt += 1) {
            if (!isRequesterIdentityCurrent(run.primaryRequester, run.identity)) return false;
            try {
                const result = await mutateReceipt(
                    'stellar:acknowledgeStellarFirstBanner',
                    {
                        cardId: banner.cardId,
                        deliveryId: run.deliveryId,
                    },
                );
                return result?.acknowledged === true;
            } catch (error) {
                if (attempt === 1) {
                    console.warn('[First Stellar] Delivery acknowledgement failed:', error);
                    return false;
                }
                await wait(ACK_RETRY_MS);
            }
        }
        return false;
    }

    function scheduleRequesters(requesters, delayMs, expectedIdentity) {
        const now = Date.now();
        const deadline = now + Math.max(250, Math.min(60_000, delayMs));
        const existing = followups.get(expectedIdentity);
        const pending = existing || {
            deadline,
            requesters: new Map(),
            timer: null,
        };
        pending.deadline = Math.min(pending.deadline, deadline);
        requesters.forEach((requester) => {
            pending.requesters.set(requester.id, requester);
        });
        if (pending.timer) clearTimeout(pending.timer);
        pending.timer = setTimeout(() => {
            followups.delete(expectedIdentity);
            const available = [...pending.requesters.values()].filter((requester) => (
                isRequesterActive(requester, expectedIdentity)
            ));
            available.forEach((requester) => {
                void request(requester);
            });
        }, Math.max(0, pending.deadline - Date.now()));
        followups.set(expectedIdentity, pending);
    }

    async function claim(run) {
        let cursor = null;
        let page = 0;
        while (true) {
            if (!isRequesterIdentityCurrent(run.primaryRequester, run.identity)) return null;
            const args = {
                deliveryId: run.deliveryId,
                language: i18n?.getLanguage?.() || 'en',
                cursor,
                pageSize: PAGE_SIZE,
            };
            let result;
            for (let attempt = 0; attempt < 2; attempt += 1) {
                try {
                    result = await mutateReceipt('stellar:claimStellarFirstBanner', args);
                    break;
                } catch (error) {
                    if (attempt === 0) {
                        await wait(CLAIM_RETRY_MS);
                        if (!isRequesterIdentityCurrent(run.primaryRequester, run.identity)) {
                            return null;
                        }
                        continue;
                    }
                    console.warn('[First Stellar] Delivery claim failed:', error);
                    scheduleRequesters(
                        [...run.requesters.values()],
                        LEASE_RETRY_MS,
                        run.identity,
                    );
                    return null;
                }
            }
            if (result?.banner) return result;
            if (
                Number.isFinite(Number(result?.retryAfterMs))
                && Number(result.retryAfterMs) >= 0
            ) return result;
            if (result?.isDone === true || !result?.continueCursor) return result || null;
            if (result.continueCursor === cursor) return null;
            cursor = result.continueCursor;
            page += 1;
            if (page % PAGE_YIELD_INTERVAL === 0) await wait(0);
        }
    }

    async function execute(run) {
        let presented = false;
        while (true) {
            const result = await claim(run);
            if (!result) return presented;
            if (
                Number.isFinite(Number(result.retryAfterMs))
                && Number(result.retryAfterMs) >= 0
            ) {
                scheduleRequesters(
                    [...run.requesters.values()],
                    Number(result.retryAfterMs) + 50,
                    run.identity,
                );
                return presented;
            }

            const banner = result.banner;
            if (!banner) return presented;
            if (
                banner.deliveryId !== run.deliveryId
                || typeof banner.cardId !== 'string'
                || !banner.cardId
            ) {
                console.error('[First Stellar] Rejected a mismatched delivery receipt.');
                return presented;
            }

            if (presented) await wait(BANNER_SPACING_MS);
            const requesters = activeRequesters(run);
            if (requesters.length === 0) {
                await release(run, banner);
                return presented;
            }
            if (typeof showAchievementBanner !== 'function') {
                await release(run, banner);
                return presented;
            }

            let imageUrl = null;
            for (const requester of requesters) {
                try {
                    imageUrl = requester.resolveImageUrl?.(banner.cardId) || imageUrl;
                } catch (_) {
                    // Card art is optional; presentation receipt correctness is not.
                }
                if (imageUrl) break;
            }

            let mounted;
            try {
                mounted = showAchievementBanner(
                    `✦ ${uiText(
                        'collection.achievements.firstStellarTitle',
                        'First Stellar!'
                    )}`,
                    uiText(
                        'collection.achievements.firstStellarSubtitle',
                        'Moonfall recorded this account as the first to forge {name} in Stellar form in the Service.',
                        { name: banner.name || banner.cardId },
                    ),
                    imageUrl,
                    'card',
                    { cardId: banner.cardId },
                );
            } catch (error) {
                console.warn('[First Stellar] Banner presenter failed:', error);
                await release(run, banner);
                return presented;
            }
            if (!mounted?.isConnected) {
                await release(run, banner);
                return presented;
            }

            presented = true;
            requesters.forEach((requester) => {
                try {
                    requester.onPresented?.(banner.cardId);
                } catch (error) {
                    console.warn('[First Stellar] Presentation observer failed:', error);
                }
            });
            if (!await acknowledge(run, banner)) {
                scheduleRequesters(
                    [...run.requesters.values()],
                    LEASE_RETRY_MS,
                    run.identity,
                );
                return true;
            }
            run.deliveryId = createDeliveryId();
        }
    }

    function request(options = {}) {
        const mutation = MoonfallCloud?.convex?.receiptMutation;
        const identity = identityFor(options);
        if (typeof mutation !== 'function' || !identity) return Promise.resolve(false);

        const requesterId = String(options.requesterId || 'default').slice(0, 64);
        const requester = {
            id: requesterId,
            requesterId,
            profileId: options.profileId || null,
            isIdentityCurrent: typeof options.isIdentityCurrent === 'function'
                ? options.isIdentityCurrent
                : () => true,
            isActive: typeof options.isActive === 'function' ? options.isActive : () => true,
            resolveImageUrl: options.resolveImageUrl,
            onPresented: options.onPresented,
        };
        if (activeRun?.identity === identity) {
            activeRun.requesters.set(requesterId, requester);
            return activeRun.promise;
        }

        const run = {
            identity,
            deliveryId: createDeliveryId(),
            primaryRequester: requester,
            requesters: new Map([[requesterId, requester]]),
            promise: null,
        };
        activeRun = run;
        run.promise = execute(run).finally(() => {
            if (activeRun === run) activeRun = null;
        });
        return run.promise;
    }

    function disposeForTests() {
        activeRun = null;
        if (delayState) {
            clearTimeout(delayState.timer);
            delayState.resolve();
        }
        followups.forEach((followup) => clearTimeout(followup.timer));
        followups.clear();
    }

    return { request, disposeForTests };
})();

export { StellarFirstBannerDelivery };
